import { RRule } from "rrule";
import { formatInTimeZone } from "date-fns-tz";
import { nextLocalMidnight } from "../../../lib/days";

const LEVEL_LABEL = { PREFERRED: "Preferred", FEASIBLE: "Feasible" };
// Ratified vote palette (UX-4): PREFERRED = green-700, FEASIBLE = blue-700 —
// the SAME AA-safe fills the scheduler calendar uses (index.css
// .scheduler-vote-fill-*; SchedulerPage eventPropGetter), so a user's marks read
// consistently across /availability and the poll page. green-700/blue-700 keep
// white mark text at WCAG AA (5.0:1 / 6.7:1).
const LEVEL_FILL  = { PREFERRED: "#15803d", FEASIBLE: "#1d4ed8" };
const LEVEL_BORDER = { PREFERRED: "#16a34a", FEASIBLE: "#3b82f6" };

// Wall-clock helpers — copy verbatim from functions/src/availability/marks.js
// to keep web-side recurrence DST-correct. See that file's comments for the
// full rationale (rrule@2.8.1 treats dtstart's UTC fields as wall-clock-in-tzid).

function utcToWallClockUtc(utcDate, tzid) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tzid,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
    hour12: false,
  });
  const parts = Object.fromEntries(
    dtf.formatToParts(utcDate).map((p) => [p.type, p.value])
  );
  return new Date(Date.UTC(
    parseInt(parts.year, 10),
    parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10),
    parseInt(parts.hour, 10),
    parseInt(parts.minute, 10),
    parseInt(parts.second, 10)
  ));
}

// The multi-day timed segment splitter (T2) advances local-day boundaries via
// the shared lib/days#nextLocalMidnight (DST-safe; the next-start delta isn't
// always 86400000 ms on a transition day). One definition keeps the segment
// splitter and the day-bucketer from drifting.

function wallClockUtcToRealUtc(wallClockAsUtc, tzid) {
  const candidate = Date.UTC(
    wallClockAsUtc.getUTCFullYear(),
    wallClockAsUtc.getUTCMonth(),
    wallClockAsUtc.getUTCDate(),
    wallClockAsUtc.getUTCHours(),
    wallClockAsUtc.getUTCMinutes(),
    wallClockAsUtc.getUTCSeconds()
  );
  const wallAtCandidate = utcToWallClockUtc(new Date(candidate), tzid).getTime();
  const offsetMs = wallAtCandidate - candidate;
  return new Date(candidate - offsetMs);
}

/**
 * Expand a list of user-authored availability marks into RBC-shaped
 * calendar events for the visible window.
 *
 * Each occurrence emits an event with:
 *   id: "mark:{markId}:{occurrenceStartIso}"   — stable across re-renders
 *   resource.kind: "mark"
 *   resource.markId, level, occurrenceStart (real-UTC ISO of this instance),
 *     isRecurring (boolean), ownerId (Phase 4b — null for the signed-in
 *     user's own marks, the share-owner uid for overlaid-friend marks).
 *
 * Recurrence is expanded against `tzid` for DST correctness (per design
 * §4.5/§6.5). Mirrors the server-side expander at functions/src/availability/marks.js;
 * duplicated locally rather than shared (per CLAUDE.md "Pragmatic Delivery
 * Priorities" at ~10-user scale).
 *
 * Multi-owner inputs (Phase 4b): pass an array containing marks from the
 * signed-in user *plus* shared-friend overlays. Each mark may carry an
 * `ownerId` (set by the caller before merging). The expander treats marks
 * uniformly — recurrence/segmentation logic is per-mark and ownership-blind.
 */
export function expandMarksToCalendarEvents(marks, windowStart, windowEnd) {
  const winStart = windowStart instanceof Date ? windowStart : new Date(windowStart);
  const winEnd = windowEnd instanceof Date ? windowEnd : new Date(windowEnd);
  const out = [];

  for (const mark of marks || []) {
    const start = mark.startUtc ? new Date(mark.startUtc) : null;
    const end = mark.endUtc ? new Date(mark.endUtc) : null;
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) continue;
    const durationMs = end.getTime() - start.getTime();

    let occurrences;
    if (!mark.rrule) {
      occurrences = (end <= winStart || start >= winEnd) ? [] : [start];
    } else {
      const tzid = mark.tzid || "UTC";
      const wallStart = utcToWallClockUtc(start, tzid);
      const wallWinStart = utcToWallClockUtc(winStart, tzid);
      const wallWinEnd = utcToWallClockUtc(winEnd, tzid);
      let rule;
      try {
        const opts = RRule.parseString(mark.rrule);
        opts.dtstart = wallStart;
        opts.tzid = tzid;
        if (mark.rruleUntil) {
          opts.until = utcToWallClockUtc(new Date(mark.rruleUntil), tzid);
        }
        rule = new RRule(opts);
      } catch {
        continue; // malformed rrule → skip this mark
      }
      const wallOccs = rule.between(wallWinStart, wallWinEnd, true);
      occurrences = wallOccs.map((w) => wallClockUtcToRealUtc(w, tzid));
    }

    const exceptions = new Set(
      Array.isArray(mark.exceptions) ? mark.exceptions : []
    );

    for (const occStart of occurrences) {
      const iso = occStart.toISOString();
      if (exceptions.has(iso)) continue;
      const userTitle = typeof mark.title === "string" ? mark.title.trim() : "";
      const occEnd = new Date(occStart.getTime() + durationMs);
      const renderTzid = mark.tzid || "UTC";

      // T2: Multi-day timed segment splitting. RBC promotes any event whose
      // start/end fall on different calendar dates to the all-day strip in
      // week/day view, regardless of allDay=false. To keep multi-day timed
      // marks visible in the time grid, emit one segment per local-day
      // touched, all carrying the same markId so click-to-edit opens the
      // canonical mark. allDay marks are left untouched — they belong in the
      // all-day strip by design.
      const segmentRanges = [];
      if (mark.allDay) {
        segmentRanges.push([occStart, occEnd]);
      } else {
        const startLocalDay = formatInTimeZone(occStart, renderTzid, "yyyy-MM-dd");
        const endLocalDay = formatInTimeZone(
          new Date(occEnd.getTime() - 1), // exclusive-end → use last covered ms
          renderTzid,
          "yyyy-MM-dd"
        );
        if (startLocalDay === endLocalDay) {
          segmentRanges.push([occStart, occEnd]);
        } else {
          let cursor = occStart;
          while (cursor < occEnd) {
            const dayBoundary = nextLocalMidnight(cursor, renderTzid);
            const realSegEnd = dayBoundary < occEnd ? dayBoundary : occEnd;
            // RBC promotes any event whose start and end fall on different
            // calendar dates to the all-day strip, regardless of allDay=false.
            // For non-final segments we end exactly at midnight; that
            // midnight is "next day" by RBC's reckoning, so trim 1ms off the
            // render-end to keep the segment same-date. The 1ms gap is
            // imperceptible. Final segment (where realSegEnd === occEnd and
            // occEnd lies within a day) is left untrimmed.
            const stoppingAtBoundary = realSegEnd === dayBoundary;
            const renderEnd = stoppingAtBoundary
              ? new Date(realSegEnd.getTime() - 1)
              : realSegEnd;
            segmentRanges.push([cursor, renderEnd]);
            cursor = realSegEnd;
          }
        }
      }

      const totalSegments = segmentRanges.length;
      segmentRanges.forEach(([segStart, segEnd], segIdx) => {
        const isSegment = totalSegments > 1;
        out.push({
          id: isSegment
            ? `mark:${mark.id}:${iso}:seg${segIdx}`
            : `mark:${mark.id}:${iso}`,
          title: userTitle || LEVEL_LABEL[mark.level] || "Mark",
          start: segStart,
          end: segEnd,
          allDay: !!mark.allDay,
          resource: {
            kind: "mark",
            markId: mark.id,
            level: mark.level,
            // Phase 4b — propagates the owning friend's uid for shared-overlay
            // marks. null for the signed-in user's own marks.
            ownerId: mark.ownerId || null,
            occurrenceStart: iso,
            isRecurring: !!mark.rrule,
            // Segment metadata — null on non-split events so existing
            // consumers (eventPropGetter, click handlers) can opt into
            // segment-aware behavior with a simple null check.
            segmentIndex: isSegment ? segIdx : null,
            totalSegments: isSegment ? totalSegments : null,
            // mark.tzid is the source of truth for what calendar day(s)
            // an allDay mark covers — needed by the SchedulerPage overlay
            // builder to produce browser-local-day-aligned bounds for
            // RBC's all-day strip (H7 fix). No-op for AvailabilityPage
            // which still consumes start/end directly.
            tzid: mark.tzid || null,
            // Phase 3b T6: forward draft flags from projectMarks so
            // eventPropGetter can apply the dashed-border "New"/"Edited"
            // visual treatment without needing the raw mark object.
            _draftCreate: mark._draftCreate || false,
            _draftPatched: mark._draftPatched || false,
          },
        });
      });
    }
  }
  return out;
}

/**
 * Style provider for `eventPropGetter`. Returns an empty style for
 * non-mark events so callers can chain it after the existing
 * buildEventStyleGetter (external/finalizedSession).
 */
export function buildMarkStyleGetter() {
  return (event) => {
    const r = event?.resource || {};
    if (r.kind !== "mark") return {};
    const fill = LEVEL_FILL[r.level] || "#64748b";
    const border = LEVEL_BORDER[r.level] || "#475569";
    // Left-edge accent bar is rendered by the .availability-mark-user-authored
    // ::before pseudo (see index.css). Setting borderLeft inline here would
    // mix shorthand+longhand and trigger React's style-conflict warning, so
    // we don't.
    return {
      style: {
        backgroundColor: fill,
        borderColor: border,
        color: "white",
        fontWeight: 600,
      },
    };
  };
}
